import React, {useEffect, useState} from 'react';
import {Chip, CircularProgress, Snackbar, Tooltip} from '@mui/material';
import {useAuthorization} from '../hooks/useAuthorization';

/**
 * Compact one-click boolean toggle for list cells (or anywhere else).
 *
 * Solves the "active / enabled / visible" pain across MRT list pages —
 * today every such toggle requires opening the entity's full edit page.
 * This component fires a single PUT for one boolean and updates in place.
 *
 * Composition contract (mirrors {@link EditableCard} — same shape, no
 * hidden behaviour):
 *
 *   <InlineToggleCell
 *     protectedBy={MinigamesPage}     // component with .abilities
 *     protectAction="edit"             // ability key
 *     value={row.enabled}              // current boolean
 *     save={(next) => apiService.put(URL, {enabled: next})}  // ⇒ Promise
 *     onSaved={(serverResult) => ...}  // optional: parent refresh hook
 *
 *     // Visual customisation (all optional, sensible defaults):
 *     labels={{on: 'Active', off: 'Inactive'}}
 *     colors={{on: 'success', off: 'default'}}
 *     tooltip="Toggle minigame visibility"
 *   />
 *
 * Behaviour:
 *   - Authorized + idle  → clickable Chip, cursor pointer.
 *   - Authorized + saving → Chip with spinner icon, click-suppressed.
 *   - Unauthorized       → static Chip (still shows state, no onClick).
 *   - Click fires {@code save(next)} OPTIMISTICALLY (UI flips first),
 *     rolls back on rejection and surfaces the error via Snackbar.
 *   - {@code stopPropagation} on click so MRT row navigation doesn't fire.
 *   - External {@code value} changes (parent refetched) re-sync local state.
 *
 * Pure props — no context other than the standard auth hook, no globals,
 * no implicit URL/payload assumptions. Caller provides the {@code save}
 * function; this component never knows what endpoint it hits.
 */
const InlineToggleCell = ({
    protectedBy,
    protectAction,
    value,
    save,
    onSaved,
    labels = {on: 'Active', off: 'Inactive'},
    colors = {on: 'success', off: 'default'},
    tooltip,
}) => {
    const {can} = useAuthorization(protectedBy);
    const authorized = can(protectAction);

    const [local, setLocal] = useState(!!value);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    // Keep in sync if the parent refetches and feeds a new value.
    useEffect(() => { setLocal(!!value); }, [value]);

    const handleClick = async (e) => {
        e.stopPropagation();   // don't bubble into row navigation
        if (!authorized || saving) return;

        const previous = local;
        const next = !previous;
        setLocal(next);        // optimistic flip
        setSaving(true);
        setError(null);
        try {
            const result = await save(next);
            onSaved?.(result);
        } catch (err) {
            setLocal(previous); // rollback
            setError(err?.message || 'Toggle failed');
        } finally {
            setSaving(false);
        }
    };

    const chip = (
        <Chip
            label={local ? labels.on : labels.off}
            color={local ? colors.on : colors.off}
            size="small"
            variant="outlined"
            icon={saving ? <CircularProgress size={12} sx={{ml: 0.5}}/> : undefined}
            onClick={authorized ? handleClick : undefined}
            sx={{
                cursor: authorized && !saving ? 'pointer' : 'default',
                pointerEvents: saving ? 'none' : undefined,
            }}
        />
    );

    return (
        <>
            {tooltip ? <Tooltip title={tooltip}>{chip}</Tooltip> : chip}
            <Snackbar
                open={!!error}
                autoHideDuration={4000}
                onClose={() => setError(null)}
                message={error}
            />
        </>
    );
};

export default InlineToggleCell;
